import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2, Rocket } from 'lucide-react';

import { Modal } from '@/components/modal';
import { useNotification } from '@/components/notifications';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CopyButton } from '@/components/ui/copy-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { listAgents } from '@/services/agents/config';
import {
  createMcpServer,
  listWizardAssets,
  listWizardWorkflows,
  McpToolDef,
  previewMcpServer,
  WizardAsset,
  WizardWorkflow,
} from '@/services/mcp-servers';

interface WizardProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onCreated: () => Promise<void> | void;
}

interface PickerItem {
  key: string;
  label: string;
  sublabel?: string;
}

const Picker = ({
  title,
  items,
  selected,
  toggle,
  loading,
}: {
  title: string;
  items: PickerItem[];
  selected: Set<string>;
  toggle: (key: string) => void;
  loading: boolean;
}) => {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? items.filter((item) => item.label.toLowerCase().includes(needle))
      : items;
    return filtered.slice(0, 50);
  }, [items, query]);

  return (
    <div className='flex flex-col gap-2 rounded-md border p-3'>
      <div className='flex items-center justify-between gap-2'>
        <h3 className='text-sm font-medium'>{title}</h3>
        <span className='text-xs text-muted-foreground'>
          {selected.size} selected
        </span>
      </div>
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={`Search ${title.toLowerCase()}…`}
      />
      <div className='max-h-44 overflow-y-auto'>
        {loading ? (
          <div className='flex items-center gap-2 py-3 text-sm text-muted-foreground'>
            <Loader2 className='size-4 animate-spin' /> Loading…
          </div>
        ) : visible.length === 0 ? (
          <p className='py-3 text-sm text-muted-foreground'>No matches.</p>
        ) : (
          visible.map((item) => (
            <label
              key={item.key}
              className='flex cursor-pointer items-start gap-2 rounded px-1 py-1.5 text-sm hover:bg-muted/50'
            >
              <Checkbox
                checked={selected.has(item.key)}
                onCheckedChange={() => toggle(item.key)}
                className='mt-0.5'
              />
              <span className='flex-1'>
                {item.label}
                {item.sublabel && (
                  <span className='block text-xs text-muted-foreground'>
                    {item.sublabel}
                  </span>
                )}
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
};

export const CreateMcpServerWizard = ({
  open,
  onClose,
  orgId,
  onCreated,
}: WizardProps) => {
  const notification = useNotification();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');

  const [workflows, setWorkflows] = useState<WizardWorkflow[]>([]);
  const [agents, setAgents] = useState<{ id: string; name: string }[]>([]);
  const [assets, setAssets] = useState<WizardAsset[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const [selectedWorkflows, setSelectedWorkflows] = useState<Set<string>>(
    new Set(),
  );
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());

  const [previewTools, setPreviewTools] = useState<McpToolDef[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createdEndpoint, setCreatedEndpoint] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !orgId) return;
    setCatalogLoading(true);
    void Promise.all([
      listWizardWorkflows(orgId).then(setWorkflows),
      listAgents(orgId)
        .then((records) =>
          setAgents(records.map((a) => ({ id: a.id, name: a.name }))),
        )
        .catch(() => setAgents([])),
      listWizardAssets(orgId).then(setAssets),
    ]).finally(() => setCatalogLoading(false));
  }, [open, orgId]);

  const reset = () => {
    setStep(1);
    setName('');
    setSlug('');
    setDescription('');
    setSelectedWorkflows(new Set());
    setSelectedAgents(new Set());
    setSelectedAssets(new Set());
    setPreviewTools([]);
    setCreatedKey(null);
    setCreatedEndpoint(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const toggle =
    (set: Set<string>, update: (next: Set<string>) => void) =>
    (key: string) => {
      const next = new Set(set);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      update(next);
    };

  const selections = () => ({
    workflows: [...selectedWorkflows].map((workflow_id) => ({ workflow_id })),
    agents: [...selectedAgents].map((agent_id) => ({ agent_id })),
    data_assets: [...selectedAssets].map((key) => {
      const [asset_type, ...rest] = key.split(':');
      return {
        asset_type: asset_type as 'collection' | 'graph' | 'snapshot',
        asset_id: rest.join(':'),
      };
    }),
  });

  const selectionCount =
    selectedWorkflows.size + selectedAgents.size + selectedAssets.size;

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const response = await previewMcpServer(orgId, selections());
      if (response.detail) {
        notification.error(response.detail);
        return false;
      }
      const unresolved = response.data?.unresolved || [];
      if (unresolved.length) {
        notification.error(
          `Some resources could not be resolved: ${JSON.stringify(unresolved)}`,
        );
        return false;
      }
      setPreviewTools(response.data?.tools || []);
      return true;
    } finally {
      setPreviewLoading(false);
    }
  };

  const create = async () => {
    setCreating(true);
    try {
      const response = await createMcpServer(orgId, {
        name: name.trim(),
        ...(slug.trim() ? { slug: slug.trim().toLowerCase() } : {}),
        description: description.trim(),
        ...selections(),
      });
      if (response.detail || !response.data?.server) {
        notification.error(response.detail || 'Creation failed');
        return;
      }
      setCreatedKey(response.data.api_key || null);
      setCreatedEndpoint(response.data.server.endpoint);
      notification.success(`MCP server "${response.data.server.name}" is live`);
      await onCreated();
    } finally {
      setCreating(false);
    }
  };

  const footer = createdEndpoint ? (
    <Button onClick={close}>Done</Button>
  ) : (
    <>
      {step > 1 && (
        <Button
          variant='outline'
          onClick={() => setStep((current) => (current - 1) as 1 | 2)}
          disabled={creating}
        >
          <ArrowLeft className='size-4' /> Back
        </Button>
      )}
      {step === 1 && (
        <Button disabled={!name.trim()} onClick={() => setStep(2)}>
          Next <ArrowRight className='size-4' />
        </Button>
      )}
      {step === 2 && (
        <Button
          disabled={selectionCount === 0 || previewLoading}
          onClick={async () => {
            if (await loadPreview()) setStep(3);
          }}
        >
          {previewLoading ? (
            <Loader2 className='size-4 animate-spin' />
          ) : (
            <ArrowRight className='size-4' />
          )}
          Preview tools
        </Button>
      )}
      {step === 3 && (
        <Button onClick={create} disabled={creating}>
          {creating ? (
            <Loader2 className='size-4 animate-spin' />
          ) : (
            <Rocket className='size-4' />
          )}
          Create server
        </Button>
      )}
    </>
  );

  return (
    <Modal
      isDialogOpen={open}
      setIsDialogOpen={(next: boolean) => {
        if (!next) close();
      }}
      title={createdEndpoint ? 'MCP server created' : 'New MCP server'}
      description={
        createdEndpoint
          ? 'The endpoint is live. Store the API key now — it is shown only once.'
          : `Step ${step} of 3 — ${
              step === 1
                ? 'name the server'
                : step === 2
                  ? 'pick the resources to expose'
                  : 'review the derived tool surface'
            }`
      }
      footer={footer}
    >
      {createdEndpoint ? (
        <div className='flex flex-col gap-3 py-2'>
          <div className='grid gap-1'>
            <Label>Endpoint</Label>
            <div className='flex items-center gap-1'>
              <code className='flex-1 rounded bg-muted px-2 py-1 text-xs'>
                {createdEndpoint}
              </code>
              <CopyButton text={createdEndpoint} size='small' />
            </div>
          </div>
          {createdKey ? (
            <div className='grid gap-1'>
              <Label>API key (shown once)</Label>
              <div className='flex items-center gap-1'>
                <code className='flex-1 rounded bg-muted px-2 py-1 text-xs break-all'>
                  {createdKey}
                </code>
                <CopyButton text={createdKey} size='small' />
              </div>
            </div>
          ) : (
            <p className='text-sm text-muted-foreground'>
              No key was minted — mint one from the server&apos;s Credentials
              tab.
            </p>
          )}
          <p className='text-sm text-muted-foreground'>
            Connection snippets for Claude/Cursor, Snowflake, and Databricks are
            on the server&apos;s Docs tab.
          </p>
        </div>
      ) : step === 1 ? (
        <div className='flex flex-col gap-3 py-2'>
          <div className='grid gap-1'>
            <Label htmlFor='mcp-name'>Name</Label>
            <Input
              id='mcp-name'
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder='Fraud Lab'
            />
          </div>
          <div className='grid gap-1'>
            <Label htmlFor='mcp-slug'>Slug (optional)</Label>
            <Input
              id='mcp-slug'
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              placeholder='fraud-lab (derived from name when empty)'
            />
            <p className='text-xs text-muted-foreground'>
              Endpoint path: /capitol-gateway/&lt;slug&gt;/mcp — lowercase
              letters, digits, hyphens; globally unique.
            </p>
          </div>
          <div className='grid gap-1'>
            <Label htmlFor='mcp-description'>Description</Label>
            <Textarea
              id='mcp-description'
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder='Shown to external MCP clients in the handshake and docs.'
              rows={3}
            />
          </div>
        </div>
      ) : step === 2 ? (
        <div className='flex flex-col gap-3 py-2'>
          <Picker
            title='Workflows'
            loading={catalogLoading}
            items={workflows.map((workflow) => ({
              key: workflow.id,
              label: workflow.name,
              sublabel: workflow.description?.slice(0, 90),
            }))}
            selected={selectedWorkflows}
            toggle={toggle(selectedWorkflows, setSelectedWorkflows)}
          />
          <Picker
            title='Agents'
            loading={catalogLoading}
            items={agents.map((agent) => ({
              key: agent.id,
              label: agent.name,
            }))}
            selected={selectedAgents}
            toggle={toggle(selectedAgents, setSelectedAgents)}
          />
          <Picker
            title='Data assets'
            loading={catalogLoading}
            items={assets.map((asset) => ({
              key: `${asset.asset_type}:${asset.asset_id}`,
              label: asset.name,
              sublabel: asset.asset_type,
            }))}
            selected={selectedAssets}
            toggle={toggle(selectedAssets, setSelectedAssets)}
          />
        </div>
      ) : (
        <div className='flex flex-col gap-2 py-2'>
          <p className='text-sm text-muted-foreground'>
            External MCP clients will see exactly these tools:
          </p>
          {previewTools.map((tool) => (
            <div key={tool.tool_name} className='rounded-md border p-3'>
              <div className='flex items-center gap-2'>
                <code className='text-sm font-medium'>{tool.tool_name}</code>
                <Badge variant='outline'>{tool.kind}</Badge>
              </div>
              <p className='mt-1 text-xs text-muted-foreground'>
                {tool.description}
              </p>
              {tool.input_schema ? (
                <details className='mt-1'>
                  <summary className='cursor-pointer text-xs text-muted-foreground'>
                    Input schema
                  </summary>
                  <pre className='mt-1 max-h-40 overflow-auto rounded bg-muted p-2 text-xs'>
                    {JSON.stringify(tool.input_schema, null, 2)}
                  </pre>
                </details>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
